// @ts-nocheck
import React, { useEffect } from 'react';
import { View, StyleSheet, ActivityIndicator } from 'react-native';
import { AuthProvider, useAuth } from './src/store/AuthContext';
import { AppProvider, useApp } from './src/store/AppStore';
import ToastHost from './src/components/Toast/Toast_Host';
import { Colors } from './src/theme/tokens';
import { getStoredTheme } from './src/components/WebShell';

// No auth screens live in this app anymore. Login, signup, email verification,
// invitations and password reset are all owned by the centralized SK Platform;
// when there's no valid session AuthContext bounces the browser there and this
// shell only ever shows the app (session valid) or a spinner (booting /
// redirecting).

// App screens
import { TicketsListScreen } from './src/screens/TicketsListScreen';
import { TicketDetailScreen } from './src/screens/TicketDetailScreen';
import { InboxScreen } from './src/screens/InboxScreen';
import { CustomersListScreen } from './src/screens/CustomersListScreen';
import { CustomerProfileScreen } from './src/screens/CustomerProfileScreen';
import { ReportsScreen } from './src/screens/ReportsScreen';
import { EmailChannelScreen } from './src/screens/admin/EmailChannelScreen';
import { TemplatesListScreen } from './src/screens/admin/TemplatesListScreen';
import { TemplateEditorScreen } from './src/screens/admin/TemplateEditorScreen';
import { GeneralSettingsScreen } from './src/screens/admin/GeneralSettingsScreen';
import { AgentsScreen } from './src/screens/admin/AgentsScreen';
import { TagsFieldsScreen } from './src/screens/admin/TagsFieldsScreen';
import { NotificationsScreen } from './src/screens/admin/NotificationsScreen';
import { ApiScreen } from './src/screens/admin/ApiScreen';
import { AutomationsScreen } from './src/screens/admin/AutomationsScreen';
import { SpacesScreen } from './src/screens/admin/SpacesScreen';
import { SpaceDetailScreen } from './src/screens/admin/SpaceDetailScreen';
import { StubScreen } from './src/screens/admin/StubScreen';
import { MyAssignmentsScreen } from './src/screens/MyAssignmentsScreen';

function AppRoutes() {
  const { route, isAssignmentOnly } = useApp();
  const { name, params = {} } = route;

  /**
   * SKTICK-DB-001 Surface A — the assignment-only user.
   *
   * A principal with zero space memberships (a franchise outlet manager) gets
   * My assignments and the ticket detail, and NOTHING ELSE. This is a routing
   * gate, not an access control — RLS already returns only their assignments.
   * Its job is to keep the console from implying the rest of the product,
   * and the spaces inside it, exist at all.
   *
   * Reports, customers and the org-wide inbox are all space- or org-scoped
   * views whose counts and totals would be drawn from queries this user must
   * not be shown the shape of. They resolve to My assignments instead of
   * erroring, because a 403 page is itself an answer about what is there.
   *
   * `null` means still loading — fall through rather than flash the wrong
   * chrome and momentarily name a space they cannot see.
   */
  if (isAssignmentOnly === true) {
    if (name === 'ticket-detail') return <TicketDetailScreen id={params.id || ''} />;
    return <MyAssignmentsScreen />;
  }

  switch (name) {
    case 'tickets-list': return <TicketsListScreen view={params.view || 'all'} />;
    case 'ticket-detail': return <TicketDetailScreen id={params.id || ''} />;
    case 'inbox': return <InboxScreen />;
    case 'customers': return <CustomersListScreen />;
    case 'customer-profile': return <CustomerProfileScreen email={params.email || ''} />;
    case 'reports': return <ReportsScreen />;
    case 'email-channel': return <EmailChannelScreen />;
    case 'email-templates': return <TemplatesListScreen />;
    case 'template-editor': return <TemplateEditorScreen slug={params.slug || ''} />;
    case 'admin-general': return <GeneralSettingsScreen />;
    case 'admin-agents': return <AgentsScreen />;
    case 'admin-tags': return <TagsFieldsScreen />;
    case 'admin-notifications': return <NotificationsScreen />;
    case 'admin-api': return <ApiScreen />;
    case 'admin-automations': return <AutomationsScreen />;
    case 'admin-spaces': return <SpacesScreen />;
    case 'space-detail': return <SpaceDetailScreen id={params.id || ''} />;
    case 'stub': return <StubScreen kind={params.kind || 'unknown'} />;
    default: return <TicketsListScreen view="all" />;
  }
}

// Decides which screen to show. Lives INSIDE AppProvider so the app shares a
// single toast host + appStoreRef bridge.
//
// Only two states remain now that ALL auth lives on the SK Platform:
//   app                       → session valid & verified → render the product
//   loading / auth / verify   → boot probe in flight, or AuthContext has already
//                               bounced to the platform auth → redirecting spinner
function Shell() {
  const { phase } = useAuth();

  const inApp = phase === 'app';

  // Pin the document theme. Inside the app we honour the user's saved choice;
  // while booting / redirecting we leave the no-flash boot value untouched.
  useEffect(() => {
    if (typeof document === 'undefined' || !inApp) return;
    document.documentElement.setAttribute('data-theme', getStoredTheme());
  }, [inApp]);

  if (inApp) {
    return (
      <View style={styles.container}>
        <AppRoutes />
      </View>
    );
  }

  // loading, or no/half session → AuthContext is redirecting to the platform auth.
  return (
    <View style={styles.center}>
      <ActivityIndicator size="large" color="#6366f1" />
    </View>
  );
}

// Mount AppProvider app-wide so the Toast host + appStoreRef bridge exist on
// EVERY screen (login/signup/verify included) — that's what makes
// showSuccess/handleApiError toasts appear on the auth screens. Authenticated
// data only loads once the user is logged in (autoLoad gate).
function AppGate() {
  const { isAuthenticated, user, isAdmin } = useAuth();
  return (
    <AppProvider autoLoad={isAuthenticated} currentUser={user} isAdmin={isAdmin}>
      <Shell />
      <ToastHost />
    </AppProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppGate />
    </AuthProvider>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Colors.paper },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
});
